import { Component, ElementRef, Input, OnInit, Renderer2 as Renderer } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AuthenticationService,
  CmdConnextService,
  CmdAzzurroHubService,
  ConnextData,
  AzzurroHubDataType,
  GenericDeviceValidator,
  MapService,
  PlantGeoInput,
  PlantPrefInput,
  ProductModelService,
  System,
  SystemsService,
  UpdatePlantSettingsInCertifiedPlantInput,
  UpdateUsersInCertifiedPlantInput,
  UserPermissions,
  checkFwVersion,
  isConnected,
  isNotNullOrUndefined,
  MonitoringModeInput,
} from 'ngx-core-services';
import { UiPromiseService, ToastFacade, DialogDataComponent } from 'ngx-zcs-components';
import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, first, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, Subject, combineLatest, of, from } from 'rxjs'
import { User } from 'firebase/auth';

import { hasPlantOwner, plantUniqueEmailValidator, POWER_VALIDATOR } from '../create-firebase/create.utils';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { selectedSystem, selectedSystemConnextData, selectedSystemAzzurroHubData, selectedSystemMetadata, SystemDefinition, SystemsState } from '../../store';
import { ICustomBlock } from '../decorators';
import { ConnextConfiguration, AzzurroHubConfiguration, virtualConfiguration } from '../configuration-section/configuration-section.models';
import { ConnextUsersForm } from '../users-section/users-section-models';
import { ConnextUserForm } from '../users-section/users-section.component';
import { PositionDetails } from '../components/maps-form/maps-form.models';
import { SettingsAwsService } from './settings-aws.service';
import { labels } from '../create-block/create.model';
import { updateFormControlIfDifferent } from '../settings-block/settings-block.utils';

export type SettingsAwsConfig = {
  system: SystemDefinition;
  virtualConfiguration: virtualConfiguration;
  connextConfiguration: ConnextConfiguration;
  azzurroHubConfiguration: AzzurroHubConfiguration;
  connextUsers: ConnextUsersForm;
  isConnextLoggerSelected: boolean;
  isAzzurroHubLoggerSelected: boolean;
  isSystemOwner: boolean;
  isSystemAdmin: boolean;
  isSuperUser: boolean;
  isConnextOffline: boolean;
  isAzzurroHubOffline: boolean;
  connextData?: Partial<ConnextData>;
  azzurroHubData?: Partial<AzzurroHubDataType>;
  connextSerial?: string;
  azzurroHubSerial?: string;
  connextMeterConfig?: FormGroup<{ sign: FormControl<number | null> }>;
  connextSensorConfig?: FormGroup<{ sign: FormControl<number | null> }>;
  connextExternalConfig?: FormGroup<{ consider: FormControl<number | null> }>;
};

@UntilDestroy()
@CreateBlockReduxSupport()
@Component({
  selector: 'settings-aws-block',
  templateUrl: './settings-aws.block.html',
  styleUrls: ['./settings-aws.block.scss'],
})
export class SettingsAwsBlock implements ICustomBlock, IReduxBlock, OnInit {

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof labels{
    return this._labels;
  }

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly selectedSystem$ = this.store.select(selectedSystem);

  readonly systemMetadata$ = this.store.select(selectedSystemMetadata).pipe(isNotNullOrUndefined());

  virtualConfiguration = new FormGroup({
    plantName: new FormControl('', Validators.required),
    coordinates: new FormControl<PositionDetails | null>(null, Validators.required),
    meterInversion: new FormControl<boolean | undefined>(undefined),
  });

  connextConfiguration = new FormGroup({
    plantName: new FormControl('', Validators.required),
    coordinates: new FormControl<PositionDetails | null>(null, Validators.required),
    plantPower: new FormControl<number | null>(null, [Validators.required, POWER_VALIDATOR]),
    plantType: new FormControl<number | null>(null, [Validators.required]),
    applied: new FormControl(false, Validators.requiredTrue),
    plantUid: new FormControl('', Validators.required),
  });

  azzurroHubConfiguration = new FormGroup({
    plantName: new FormControl('', Validators.required),
    coordinates: new FormControl<PositionDetails | null>(null, Validators.required),
    contractualPower: new FormControl<number | null>(null, [Validators.required, POWER_VALIDATOR]),
    plantType: new FormControl<number | null>(null, [Validators.required]),
    monitoringMode: new FormControl<MonitoringModeInput | null>(null, [Validators.required]),
    applied: new FormControl(false, Validators.requiredTrue),
    plantUid: new FormControl('', Validators.required),
    isOnline: new FormControl<boolean | null>(null),
  });

  readonly plantUsers = new FormGroup({
    users: new FormArray([
      new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email, plantUniqueEmailValidator]),
        userLevel: new FormControl(UserPermissions.viewer, Validators.required),
        isSelected: new FormControl(false),
        added: new FormControl(false, Validators.requiredTrue),
      }),
    ]),
    isModified: new FormControl(false),
  }, {
    validators: hasPlantOwner,
  });

  connextMeterConfig = new FormGroup({
    // -1: inverted, 1: normal
    // validator only null, -1, 1
    sign: new FormControl<null| number>(null, [Validators.required, Validators.pattern(/^-1|1$/)]),
  });

  connextSensorConfig = new FormGroup({
    // -1: inverted, 1: normal
    sign: new FormControl<null| number>(null, [Validators.required, Validators.pattern(/^-1|1$/)]),
  });

  connextExternalConfig = new FormGroup({
    // 0: do not consider, 1: consider
    consider: new FormControl<null| number>(null, [Validators.required, Validators.pattern(/^0|1$/)]),
  });

  readonly loggedUser$ = this.authenticationService.user$.pipe(
    isNotNullOrUndefined(),
  );

  readonly updateFormControlIfDifferentByConnextForm = updateFormControlIfDifferent(this.connextConfiguration);
  readonly updateFormControlIfDifferentByAzzurroHubForm = updateFormControlIfDifferent(this.azzurroHubConfiguration);
  readonly updateFormControlIfDifferentByVirtualForm = updateFormControlIfDifferent(this.virtualConfiguration);

  /**
   * @important This button is only used to save name and position changes
   * so it's fine for monitoring type Solarman, connext offline and connext users without permission.
   */
  readonly showSaveButton$ = combineLatest([
    this.selectedSystem$,
    this.loggedUser$,
    this.systemMetadata$,
  ]).pipe(
    map(([systemD, currentUser]) => {
      if (!systemD) {
        return false;
      }

      if( systemD.monitoringType === 'Solarman') {
        return true;
      }

      // Azzurro Hub systems
      if (this.isAzzurroHubSystem(systemD)) {
        const azzurroHubIsOnline = systemD.metadata?.isConnected;

        // Se offline, permetti salvataggio (configurazione locale)
        if (!azzurroHubIsOnline) {
          return true;
        }

        // Se online, controlla permessi utente
        const currentUserInSystem = systemD.aws?.users?.find((user) => user.email === currentUser.email);
        const userIsAdmin = currentUserInSystem?.userLevel === UserPermissions.Admin;
        const userIsOwner = currentUserInSystem?.userLevel === UserPermissions.Owner;
        const userIsSuperUsers = currentUserInSystem?.userLevel === UserPermissions.hidden;

        return userIsOwner || userIsAdmin || userIsSuperUsers;
      }

      if (!systemD.data) {
        return false;
      }

      const connextIsOnline = systemD.metadata?.isConnected;
      if (!connextIsOnline) {
        return true;
      }

      const currentUserInSystem = systemD.aws?.users?.find((user) => user.email === currentUser.email);
      const userIsAdmin = currentUserInSystem?.userLevel === UserPermissions.Admin;
      const userIsOwner = currentUserInSystem?.userLevel === UserPermissions.Owner;
      const userIsSuperUsers = currentUserInSystem?.userLevel === UserPermissions.hidden;
      return !userIsOwner && !userIsAdmin && !userIsSuperUsers;
    }),
  );

  readonly showRemoveMeButton$ = combineLatest([
    this.loggedUser$,
    this.system$,
  ]).pipe(
    map(([currentUser, system]) => {
      if (!system) {
        return false;
      }
      const currentUserInSystem = system.aws?.users?.find((user) => user.email === currentUser.email);
      const owners = system.aws?.users?.filter((user) => user.userLevel === UserPermissions.Owner);

      if( currentUserInSystem?.userLevel === UserPermissions.Owner && owners?.length === 1 ) {
        return false;
      }

      return true;
    }),
  );


  readonly connextData = this.store.select(selectedSystemConnextData);

  readonly azzurroHubData = this.store.select(selectedSystemAzzurroHubData);

  /**
   * Prevents updateConfiguration$ from emitting while a user mutation is in progress.
   * Without this, the subscription can emit intermediate/stale data during save,
   * causing the FormArray to be rebuilt multiple times and blocking the UI.
   */
  private readonly savingUsers$ = new BehaviorSubject<boolean>(false);

  readonly updateConfiguration$: Observable<SettingsAwsConfig | null | undefined> = combineLatest([
    this.selectedSystem$,
    this.connextData,
    this.azzurroHubData,
    this.loggedUser$,
    this.systemMetadata$,
    this.savingUsers$,
  ]).pipe(
    filter(([, , , , , isSaving]) => !isSaving),
    switchMap(([system, connextData, azzurroHubData, currentUser]) => {
      if (!system) {
        return of(null);
      }
      return from(this.updateConfiguration(system, currentUser, connextData, azzurroHubData as Partial<AzzurroHubDataType> | null));

    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly showMeterInversion$ = combineLatest([
    this.systemMetadata$,
    this.updateConfiguration$.pipe(isNotNullOrUndefined()),
  ]).pipe(
    map(([metadata, config]) => {
      return metadata.invertMeterSensor !== undefined && (config.isSystemOwner || config.isSystemAdmin || config.isSuperUser);
    }),
  );

  // Observable instead of getter — evaluated only when the form status changes,
  // not on every change detection cycle (the getter was called hundreds of times at rest).
  readonly isUsersValid$ = this.plantUsers.statusChanges.pipe(
    startWith(this.plantUsers.status),
    map(() => !this.plantUsers.errors?.hasPlantOwner),
    distinctUntilChanged(),
  );

  // Save is enabled only when there are pending modifications AND at least one owner exists.
  readonly canSaveUsers$ = combineLatest([
    this.isUsersValid$,
    this.plantUsers.controls.isModified.valueChanges.pipe(
      startWith(this.plantUsers.controls.isModified.value),
    ),
  ]).pipe(
    map(([isValid, isModified]) => isValid && isModified),
    distinctUntilChanged(),
  );

  constructor(
    private readonly cmdConnextService: CmdConnextService,
    private readonly cmdAzzurroHubService: CmdAzzurroHubService,
    private readonly dialog: MatDialog,
    private readonly genericDeviceValidator: GenericDeviceValidator,
    private readonly mapService: MapService,
    private readonly toastFacade: ToastFacade,
    private readonly uiPromiseService: UiPromiseService,
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer,
    readonly route: ActivatedRoute,
    readonly systemsService: SystemsService,
    private readonly authenticationService: AuthenticationService,
    private readonly settingsAwsService: SettingsAwsService,
  ) {
    this.updateSystemSettings = this.updateSystemSettings.bind(this);
  }

  ngOnInit() {
    combineLatest([
      this.systemsService.systems$,
      this.route.params,
    ]).pipe(
      untilDestroyed(this),
    ).subscribe(([systems, params]) => {
      const uid = params.uid;
      const systemsNotValid = systems?.filter((system) => system.uid !== uid);
      this.virtualConfiguration.controls.plantName.setAsyncValidators([
        this.genericDeviceValidator.deviceUnique(
          systemsNotValid,
          async (value) => value,
          'name',
        ),
      ]);

      this.connextConfiguration.controls.plantName.setAsyncValidators([
        this.genericDeviceValidator.deviceUnique(
          systemsNotValid,
          async (value) => value,
          'name',
        ),
      ]);

      this.azzurroHubConfiguration.controls.plantName.setAsyncValidators([
        this.genericDeviceValidator.deviceUnique(
          systemsNotValid,
          async (value) => value,
          'name',
        ),
      ]);
    });
  }

  async updateConfiguration(system: SystemDefinition, currentUser: User, connextConfig?: Partial<ConnextData> | null, azzurroHubConfig?: Partial<AzzurroHubDataType> | null): Promise<SettingsAwsConfig> {
    const plantName = system.name;
    const meterInversion = system.metadata?.invertMeterSensor;
    const uid = system.uid as string;
    const position = {
      lat: system.metadata?.location?.lat ? +(system.metadata.location.lat) : 0,
      lng: system.metadata?.location?.lng ? +(system.metadata.location.lng) : 0,
    }
    const latLng = await this.mapService.getLatLng(position);

    /**
     * Users section
    */
    const users = system.aws?.users;
    if (users?.length) {
      // Only rebuild the FormArray if the server data actually changed and the user
      // has no pending modifications. This avoids unnecessary DOM destruction of
      // mat-select dropdowns and prevents losing unsaved changes.
      const currentFingerprint = this.plantUsers.controls.users.value
        .filter((u) => u.added)
        .map((u) => `${u.email}|${u.userLevel}`)
        .join(',');
      const newFingerprint = users
        .map((u) => `${u.email}|${u.userLevel}`)
        .join(',');
      const usersChanged = currentFingerprint !== newFingerprint;

      if (usersChanged && !this.plantUsers.controls.isModified.value) {
        this.plantUsers.controls.users.clear();
        users.forEach((user) => {
          this.plantUsers.controls.users.push(new FormGroup({
            email: new FormControl(user.email, [Validators.required, Validators.email, plantUniqueEmailValidator]),
            userLevel: new FormControl(user.userLevel, Validators.required),
            isSelected: new FormControl(false),
            added: new FormControl(true),
          }));
        });

        this.plantUsers.controls.users.push(new FormGroup({
          email: new FormControl('', [Validators.required, Validators.email, plantUniqueEmailValidator]),
          userLevel: new FormControl<number>(UserPermissions.viewer, Validators.required),
          isSelected: new FormControl(false),
          added: new FormControl(false),
        }));

        this.plantUsers.controls.isModified.setValue(false);
      }
    }

    const currentUserInSystem = users?.find((user) => user.email === currentUser.email);

    const userIsViewer = currentUserInSystem?.userLevel === UserPermissions.viewer;
    const userIsAdmin = currentUserInSystem?.userLevel === UserPermissions.Admin;
    const userIsSuperUsers = currentUserInSystem?.userLevel === UserPermissions.hidden;
    const userIsOwner = currentUserInSystem?.userLevel === UserPermissions.Owner;

    if(system.monitoringType === 'Solarman' || userIsViewer) {
      this.updateFormControlIfDifferentByVirtualForm({
        plantName,
        coordinates: {
          latLng,
          timezone: null,
        },
        meterInversion: meterInversion ?? null,
      });

      if ( userIsViewer ) {
        this.virtualConfiguration.controls.coordinates.disable();
      }

      return {
        system,
        virtualConfiguration: this.virtualConfiguration,
        connextConfiguration: this.connextConfiguration,
        azzurroHubConfiguration: this.azzurroHubConfiguration,
        connextUsers: this.plantUsers,
        isConnextLoggerSelected: false,
        isAzzurroHubLoggerSelected: false,
        isConnextOffline: false,
        isAzzurroHubOffline: false,
        isSystemAdmin: userIsAdmin,
        isSuperUser: userIsSuperUsers,
        isSystemOwner: userIsOwner,
      };
    }

    // Azzurro Hub configuration
    const isAzzurroHubSystem = this.isAzzurroHubSystem(system);
    if (isAzzurroHubSystem) {
      // Popola il form con i dati disponibili o valori di default
      this.updateFormControlIfDifferentByAzzurroHubForm({
        plantName,
        coordinates: {
          latLng,
          timezone: null,
        },
        contractualPower: azzurroHubConfig?.config?.maxContractualPwr || 3000, // default 3kW
        plantType: azzurroHubConfig?.config?.plantPhases === 'threePhase' ? 1 : 0,
        monitoringMode: azzurroHubConfig?.config?.monitoringMode || 'invMaster',
        applied: !!azzurroHubConfig, // true se abbiamo dati, false se dobbiamo configurare
        plantUid: uid,
      });

      return {
        system,
        virtualConfiguration: this.virtualConfiguration,
        connextConfiguration: this.connextConfiguration,
        azzurroHubConfiguration: this.azzurroHubConfiguration,
        connextUsers: this.plantUsers,
        isConnextLoggerSelected: false,
        isAzzurroHubLoggerSelected: true,
        isConnextOffline: false,
        isAzzurroHubOffline: azzurroHubConfig?.info?.connection?.status !== 'connected',
        isSystemAdmin: userIsAdmin,
        isSuperUser: userIsSuperUsers,
        isSystemOwner: userIsOwner,
        azzurroHubData: azzurroHubConfig || undefined,
        azzurroHubSerial: system.configuration?.azzurroHubId as string,
      };
    }

    const sensor_mode = connextConfig?.config?.sensor_mode;
    const devices = connextConfig?.devices || [];
    const thereAreThreePhases = devices.some((device) => {
      const product = new ProductModelService(device.sn);
      return product.type?.name === 'inverter' && product.type.phase === 'three-phase';
    });
    const thereAreNoZCS = devices.some((device) => {
      const product = new ProductModelService(device.sn);
      return product.type?.name === 'no-zcs';
    });
    const thereAreHybridInverters = devices.some((device) => {
      const product = new ProductModelService(device.sn);
      return product.type?.name === 'inverter' && (product.type.value === 'hybrid-3ph' || product.type.value === 'hybrid');
    });

    /**
     * (SENS2_K CONNEXT << 4 | SENS1_METER	), // 0
     * (SENS2_CONNEXT  << 4 | SENS1_CONNEXT),	 // 1
     * (SENS2_INVERTER << 4 | SENS1_CONNEXT),	 // 2
     * (SENS2_INVERTER << 4 | SENS1_METER  ),	 // 3
     * (SENS2_INVERTER << 4 | SENS1_INVERTER), // 4 || 3
     * (SENS2_INVERTER << 4 | SENS1_METER)		// 5
     *
     * If production sensor is enabled, we block the phase selection,
     * same thing if there are three phases inverters
     */
    const phaseBlocked = (['0', '1'].includes(`${sensor_mode}`) && thereAreNoZCS) || thereAreThreePhases;
    const phase = connextConfig?.config?.pType || 0;
    const power = (connextConfig?.config?.maxLoadPwr || 0) * 100;

    this.updateFormControlIfDifferentByConnextForm({
      plantName,
      coordinates: {
        latLng,
        timezone: null,
      },
      plantPower: power,
      plantType: phase,
      applied: true,
      plantUid: uid,
    });

    if (phaseBlocked) {
      this.connextConfiguration.controls.plantType.disable();
    }

    const settingsConfig: SettingsAwsConfig = {
      system,
      connextData: connextConfig!,
      virtualConfiguration: this.virtualConfiguration,
      connextConfiguration: this.connextConfiguration,
      azzurroHubConfiguration: this.azzurroHubConfiguration,
      connextUsers: this.plantUsers,
      isConnextLoggerSelected: true,
      isAzzurroHubLoggerSelected: false,
      isConnextOffline: !isConnected(connextConfig),
      isAzzurroHubOffline: false,
      isSystemAdmin: userIsAdmin,
      isSystemOwner: userIsOwner,
      isSuperUser: userIsSuperUsers,
      connextSerial: system.configuration.connextId as string,
      connextMeterConfig: undefined,
      connextSensorConfig: undefined,
      connextExternalConfig: undefined,
    };

    const connextFwVersion = connextConfig?.info?.fw.fw_ver;
    const supportChangeSensorSignAndExternalConfig = checkFwVersion(connextFwVersion, '1.40.0000');
    if (sensor_mode !== 4 && supportChangeSensorSignAndExternalConfig) {
      if (phase === 1) {
        this.updateFormControlIfDifferentByConnextForm({
          sign: connextConfig?.config?.mt_sign as number,
        });
        settingsConfig.connextMeterConfig = this.connextMeterConfig;
      } else {
        this.updateFormControlIfDifferentByConnextForm({
          sign: connextConfig?.config?.ta_sign as number,
        });
        settingsConfig.connextSensorConfig = this.connextSensorConfig;
      }
    }

    if (thereAreHybridInverters && supportChangeSensorSignAndExternalConfig) {
      this.updateFormControlIfDifferentByConnextForm({
        consider: connextConfig?.config?.pext as number,
      });
      settingsConfig.connextExternalConfig = this.connextExternalConfig;
    }

    return settingsConfig;
  }

  async updateUsersInPlant(config: SettingsAwsConfig, loggedUser: User) {
    const updateFunction = async () => {
      await this.mutateUsersAndWaitForSubscription(config.system.uid!, users);
      const serverEmails = new Set((config.system.aws?.users ?? []).map((u) => u.email));
      const hasNewUsers = users.some((u) => !serverEmails.has(u.email));
      await this.toastFacade.present(
        hasNewUsers ? this.labels.usersAddedToastContent : this.labels.usersUpdatedToastContent,
        hasNewUsers ? this.labels.usersAddedToastTitle : this.labels.usersUpdatedToastTitle,
        { type: 'browser', browserMode: 'simple', severity: 'success' },
      );
    }

    const systemDefinition = await this.store.select(selectedSystem).pipe(first()).toPromise();
    const myCurrentPermissions = systemDefinition?.aws?.users?.find((user) => user.email === loggedUser.email)?.userLevel;

    const users = this.plantUsers.controls.users.value
      .filter((user) => user.email !== '')
      .map((user) => ({
        email: user.email!,
        userLevel: user.userLevel!,
      }));

    const myPermissionsRequested = users.find((user) => user.email === loggedUser.email)?.userLevel;

    if ( myCurrentPermissions === myPermissionsRequested ) {
      return await updateFunction();
    }

    this.dialog.open(DialogDataComponent, {
      width: '600px',
      data: {
        title: labels.userWarningTitle,
        content: labels.currentUserUpdatePermissionsWarning,
        buttons: [
          {
            text: this.labels.cancel,
            action: () => {},
          },
          {
            text: labels.UpdateUserPermissionAction,
            cssClass: 'custom-mat-button-warning test-delete-confirm',
            focus: true,
            action: updateFunction,
          },
        ],
      },
    });
  }

  /**
   * Handles user deletion with confirmation dialog.
   * Only saved users (that exist on the server) reach this method — unsaved users
   * are discarded directly in users-section via the inline remove button.
   */

  /** Handles the Add button: sends mutation immediately with all current users + the new one. */
  async onUserAdded(formData: ConnextUsersForm, config: SettingsAwsConfig) {
    const allUsers = formData.controls.users.value
      .filter((user) => user.email !== '')
      .map((user) => ({
        email: user.email!,
        userLevel: user.added ? user.userLevel! : UserPermissions.viewer,
      }));

    if (!allUsers.length) {
      return;
    }

    await this.mutateUsersAndWaitForSubscription(config.system.uid!, allUsers);
    await this.toastFacade.present(
      this.labels.usersAddedToastContent,
      this.labels.usersAddedToastTitle,
      { type: 'browser', browserMode: 'simple', severity: 'success' },
    );
  }

  async deleteSelectedUsers(emails: string[], config: SettingsAwsConfig, loggedUser: User) {
    const isDeletingSelf = emails.includes(loggedUser.email!);
    const confirmed = await this.showDeleteUsersConfirmation(emails, isDeletingSelf);
    if (!confirmed) {
      return;
    }
    await this.executeDeleteUsers(emails, config);
    await this.toastFacade.present(
      this.labels.usersRemovedToastContent,
      this.labels.usersRemovedToastTitle,
      { type: 'browser', browserMode: 'simple', severity: 'success' },
    );
  }

  private showDeleteUsersConfirmation(emails: string[], isDeletingSelf: boolean): Promise<boolean> {
    return new Promise((resolve) => {
      const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const emailList = emails.map((e) => `<strong>${escapeHtml(e)}</strong>`).join(', ');
      const dialogRef = this.dialog.open(DialogDataComponent, {
        data: {
          title: this.labels.removeUsersDialogTitle,
          content: isDeletingSelf
            ? this.labels.currentUserUpdatePermissionsWarning
            : this.labels.removeUsersDialogContent.replace('{{emails}}', emailList),
          buttons: [
            {
              text: this.labels.cancel,
              cssClass: 'custom-mat-button-secondary',
              closeDialogOnAction: true,
              action: () => {
                resolve(false);
              },
            },
            {
              text: this.labels.removeUsersDialogAction,
              cssClass: 'custom-mat-button-warning',
              focus: true,
              closeDialogOnAction: true,
              action: () => {
                resolve(true);
              },
            },
          ],
        },
      });

      dialogRef.afterClosed().subscribe(() => resolve(false));
    });
  }

  private async executeDeleteUsers(emails: string[], config: SettingsAwsConfig) {
    const remainingUsers = this.plantUsers.controls.users.value
      .filter((user) => user.email !== '' && user.added && !emails.includes(user.email!))
      .map((user) => ({ email: user.email!, userLevel: user.userLevel! }));

    // Ensure at least one Owner remains after deletion
    const hasOwner = remainingUsers.some((u) => u.userLevel === UserPermissions.Owner);
    if (!hasOwner) {
      await this.toastFacade.present(
        this.labels.atLeastOneOwner,
        this.labels.usersErrorToastTitle,
        { type: 'browser', browserMode: 'simple', severity: 'error' },
      );
      return;
    }

    await this.mutateUsersAndWaitForSubscription(config.system.uid!, remainingUsers);
  }

  /**
   * Sends the user mutation, then waits for the subscription to propagate the updated
   * data through the store before closing the spinner. This avoids re-fetching and
   * ensures a single FormArray rebuild with correct data.
   *
   * Flow:
   * 1. Block updateConfiguration$ (savingUsers$ = true)
   * 2. Send mutation
   * 3. Unblock updateConfiguration$ (savingUsers$ = false)
   * 4. Wait for selectedSystem$ to emit with users matching what we sent
   * 5. Spinner closes — one clean FormArray rebuild
   */
  private async mutateUsersAndWaitForSubscription(plantId: string, expectedUsers: { email: string; userLevel: number }[]) {
    this.savingUsers$.next(true);

    const expectedFingerprint = expectedUsers
      .map((u) => `${u.email}|${u.userLevel}`)
      .sort()
      .join(',');

    await this.uiPromiseService.run(async () => {
      try {
        await this.settingsAwsService.updateUsersInSystem({ plantId, users: expectedUsers });

        // Unblock updateConfiguration$ so subscription data can flow through the store.
        // Keep isModified = true so the fingerprint guard blocks rebuilds with stale store
        // data (the store still has old data until the subscription arrives).
        this.savingUsers$.next(false);

        // Wait for updateConfiguration$ to emit with the expected user data.
        // Unlike selectedSystem$ (which only confirms the store has been updated),
        // updateConfiguration$ guarantees the FormArray has been rebuilt inside
        // updateConfiguration() before emitting — so the UI is fully up to date.
        await this.updateConfiguration$.pipe(
          map((config) => config?.system?.aws?.users
            ?.map((u: any) => `${u.email}|${u.userLevel}`)
            .sort()
            .join(',')
          ),
          filter((fingerprint) => fingerprint === expectedFingerprint),
          first(),
        ).toPromise();

        // Sort the FormArray alphabetically by email before closing the spinner,
        // so the UI shows the final sorted list immediately.
        const usersControl: FormArray<ConnextUserForm> = this.plantUsers.controls.users;
        const sorted = usersControl.controls
          .filter((c) => c.value.added)
          .sort((a, b) => (a.value.email ?? '').localeCompare(b.value.email ?? ''));
        const emptyRow = usersControl.controls.find((c) => !c.value.added);
        usersControl.clear();
        sorted.forEach((c) => usersControl.push(c));
        if (emptyRow) {
          usersControl.push(emptyRow!);
        }

        this.plantUsers.controls.isModified.setValue(false);
      } catch (error) {
        // Ensure updateConfiguration$ is unblocked even on failure,
        // otherwise the UI stays permanently frozen.
        this.savingUsers$.next(false);
        throw error;
      }
    }, {
      loading: true,
      error: {
        title: this.labels.usersErrorToastTitle,
        message: this.labels.usersErrorToastContent,
      },
    });
  }

  async updateSystemSettings(config: SettingsAwsConfig) {
    this.uiPromiseService.run(async () => {
      const system = config.system;

      // Determina quale configurazione usare
      let plantConfigurations: FormGroup;

      if (system.monitoringType === 'Solarman' || !(config.isSystemOwner || config.isSystemAdmin || config.isSuperUser)) {
        // Virtual system o utente senza permessi
        plantConfigurations = this.virtualConfiguration;
      } else if (config.isAzzurroHubLoggerSelected && !config.isAzzurroHubOffline) {
        // Azzurro Hub system online con permessi
        plantConfigurations = this.azzurroHubConfiguration;
      } else if (config.isConnextLoggerSelected && !config.isConnextOffline) {
        // Connext system online con permessi
        plantConfigurations = this.connextConfiguration;
      } else {
        // Fallback: virtual configuration
        plantConfigurations = this.virtualConfiguration;
      }

      const plantNameControl = plantConfigurations.controls.plantName;
      const coordinatesControl = plantConfigurations.controls.coordinates;

      if (plantNameControl.invalid || coordinatesControl.invalid) {
        return;
      }

      const plantName = plantNameControl.value;
      const coordinates = coordinatesControl.value;

      if ( !coordinates || !coordinates.latLng || !plantName ) {
        return;
      }

      if (!coordinates.timezone) {
        const timezone = await this.mapService.getTz(coordinates.latLng);
        if (!timezone) {
          return;
        }
        coordinates.timezone = timezone;
      }

      const plantPref: PlantPrefInput = {
        plantName: plantConfigurations.value.plantName!,
      }

      if ( !config.isSystemOwner && !config.isSuperUser && !config.isSystemAdmin ) {
        const viewerUpdateInput = {
          plantId: system.uid!,
          plantPref,
        };

        await this.settingsAwsService.updateSystemSettings(viewerUpdateInput);
      } else {
        /**
         * If null or undefined ignore the field, otherwise send it
         */
        const config = (this.virtualConfiguration.value.meterInversion !== undefined && this.virtualConfiguration.value.meterInversion !== null)
          ? {
              meterInversion: this.virtualConfiguration.value.meterInversion as boolean,
            }
          : undefined

        const geo: PlantGeoInput= {
          latitude: coordinates.latLng.lat()!,
          longitude: coordinates.latLng.lng()!,
          IANA: coordinates.timezone?.zoneName!,
        }

        const adminUpdateInput: UpdatePlantSettingsInCertifiedPlantInput = {
          plantId: system.uid!,
          plantPref,
          geo,
          config,
        };
        await this.settingsAwsService.updateSystemSettings(adminUpdateInput);
      }
    },
    {
      loading: true,
    });
  }

  async deleteSystem(config: SettingsAwsConfig, loggedUser: User) {
    if(config.system.monitoringType === 'Solarman') {
      this.deleteVirtualSystem(config);
    } else if (this.isAzzurroHubSystem(config.system)) {
      this.deleteAzzurroHubSystem(config);
    } else {
      this.deleteConnextSystem(config);
    }
  }

  async removeMeToSystem(config: SettingsAwsConfig, loggedUser: User) {
    const primaryActionFn = async () =>
      this.uiPromiseService.run(() => this.deleteMyUserByAwsSystem(config, loggedUser), {
        loading: true,
        loadingMessage: this.labels.deletingPlant,
    });

    await this.openDialogToDeleteSystem(
      this.labels.deletePlantDialogTitle,
      this.labels.deletePlantDialogContentVirtual,
      primaryActionFn,
    );
  }

  async deleteConnextSystem(config: SettingsAwsConfig) {

    if (!isConnected(config.connextData)) {
      return this.toastFacade.present(
        this.labels.connextOfflineToastContent,
        this.labels.connextOfflineToastTitle,
        {
          type: 'browser',
          browserMode: 'persistent',
          severity: 'error',
        },
      );
    }
    const connextId = config.system.configuration.connextId as string;

    const primaryActionFn = async () => {
      const noAutoOwner = false;
      const advancedMode = false;
      const cmd = 'set default all';
      if (!config.system.uid) {
        return;
      }

      await this.cmdConnextService.sendCommandInPlantStatusWithService(config.system.uid, connextId, cmd);
      this.uiPromiseService.run(() => this.deleteSystemByAws(config), {
        loading: true,
        loadingMessage: this.labels.deletingPlant,
      });
    };

    await this.openDialogToDeleteSystem(
      this.labels.deletePlantDialogTitle,
      this.labels.deletePlantDialogContentWithConnext,
      primaryActionFn,
    );
  }

  async deleteVirtualSystem(config: SettingsAwsConfig) {
    const primaryActionFn = async () =>
      await this.uiPromiseService.run(() =>
        this.deleteSystemByAws(config), {
          loading: true,
          loadingMessage: this.labels.deletingPlant,
      });

    await this.openDialogToDeleteSystem(
      this.labels.deletePlantDialogTitle,
      this.labels.deletePlantDialogContentVirtual,
      primaryActionFn,
    );
  };

  /**
   * Eliminazione sistema Azzurro Hub
   * NOTA: Per Azzurro Hub non viene inviato nessun comando di reset,
   * a differenza di Connext. Solo eliminazione dal sistema AWS.
   */
  async deleteAzzurroHubSystem(config: SettingsAwsConfig) {
    const primaryActionFn = async () => {
      if (!config.system.uid) {
        return;
      }

      try {
        // Elimina sistema da AWS (nessun comando inviato ad Azzurro Hub)
        await this.uiPromiseService.run(
          () => this.deleteSystemByAws(config),
          {
            loading: true,
            loadingMessage: this.labels.deletingPlant,
          }
        );

      } catch (error) {
        console.error('Errore eliminazione sistema Azzurro Hub:', error);
        this.toastFacade.present(
          'Error deleting Azzurro Hub system',
          'Error',
          { type: 'browser', severity: 'error' }
        );
      }
    };

    // Dialog di conferma
    await this.openDialogToDeleteSystem(
      this.labels.deletePlantDialogTitle,
      'This will permanently delete the plant from the system. The Azzurro Hub device will not be modified.',
      primaryActionFn
    );
  }

  async deleteSystemByAws(config: SettingsAwsConfig) {
    const uid = config.system.uid as string;

    await this.settingsAwsService.deleteSystem(uid);
    await this.toastFacade.present(
      this.labels.deletePlantToastContent,
      this.labels.deletePlantToastTitle,
      {
        type: 'browser',
        browserMode: 'persistent',
        severity: 'success',
      },
    );
  }

  async deleteMyUserByAwsSystem(config: SettingsAwsConfig, loggedUser: User) {

    const plantUser = this.plantUsers.controls.users.value.find((user) => user.email === loggedUser.email);
    if (!plantUser || plantUser.userLevel === undefined || plantUser.userLevel === null || !loggedUser.email) {
      throw new Error('User not found');
    }

    await this.settingsAwsService.removeUserFromSystem({
      email: loggedUser.email,
      userLevel: plantUser.userLevel,
    }, config.system.uid!);

    await this.toastFacade.present(
      this.labels.deletePlantToastContent,
      this.labels.deletePlantToastTitle,
      {
        type: 'browser',
        browserMode: 'persistent',
        severity: 'success',
      },
    );
  }

  async openDialogToDeleteSystem(title: string, content: string, primaryActionFn: () => Promise<any>) {
    const dialogRef = this.dialog.open(DialogDataComponent, {
      width: '600px',
    });
    const actions = [
      {
        text: this.labels.cancel,
        action: () => dialogRef.close(),
      },
      {
        text: this.labels.deletePlantDialogAction,
        cssClass: 'custom-mat-button-warning test-delete-confirm',
        focus: true,
        action: () => {
          primaryActionFn();
          dialogRef.close();
        },
      },
    ];

    dialogRef.componentInstance.data = {
      title,
      content,
      buttons: actions,
    };
  }

  /**
   * Determina se il sistema è di tipo Azzurro Hub
   * Usato per nascondere sezioni non supportate (es. notifiche)
   */
  isAzzurroHubSystem(system: SystemDefinition): boolean {
    // Verifica presenza azzurroHubId nella configurazione
    if (system.configuration?.azzurroHubId) {
      return true;
    }

    // Verifica monitoring type
    if (system.monitoringType === 'AzzurroHub') {
      return true;
    }

    // Verifica se è un sistema con 'azzurroHub' nel nome o altre proprietà caratteristiche
    // Aggiungi qui altri controlli se necessario basati su come vengono identificati i sistemi Azzurro Hub

    return false;
  }
}
